/**
 * Ce que l'organisation autorise à la tablette, **avec ses défauts tranchés une seule fois**.
 *
 * ⚠️ **Le défaut est celui du fonctionnement d'aujourd'hui, pas le plus permissif** : une
 * tablette qui n'a jamais resynchronisé — ou dont le bundle précède le champ — doit se comporter
 * comme avant, pas ouvrir un geste que le serveur refusera. Même parti que
 * `readDevicePreferences`, et pour la même raison : sans ce point unique, chaque lecteur
 * inventerait son défaut et une mise à jour de l'app changerait le comportement de tout le parc.
 */
import type { ServerRules, SharedDatabase } from './shared-db'

export interface ResolvedServerRules {
  allowAttachmentsAfterSubmission: boolean
}

/** Les défauts : ce que la tablette fait quand le serveur n'a encore rien dit. */
export const DEFAULT_SERVER_RULES: ResolvedServerRules = {
  allowAttachmentsAfterSubmission: false,
}

export function resolveServerRules(rules: ServerRules | undefined): ResolvedServerRules {
  return {
    allowAttachmentsAfterSubmission:
      rules?.allowAttachmentsAfterSubmission ??
      DEFAULT_SERVER_RULES.allowAttachmentsAfterSubmission,
  }
}

/**
 * Les règles telles que la tablette les connaît.
 *
 * ⚠️ **Ne lève jamais** : une base illisible — évincée, ou fermée par une mise à jour — rend les
 * défauts plutôt que de faire tomber l'écran qui l'interroge (M21). Un onglet qui refuserait de
 * s'afficher parce qu'un booléen manque serait pire que le booléen manquant.
 */
export async function readServerRules(db: SharedDatabase): Promise<ResolvedServerRules> {
  try {
    return resolveServerRules(await db.serverRules.get('current'))
  } catch {
    return DEFAULT_SERVER_RULES
  }
}
